/**
 * Reports Service
 *
 * Handles reports-related API calls.
 */

import config from '../config';
import api, { getApiUrl, getHeaders } from '../utils/api';

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1].trim();
  const unquoted = header.match(/filename=([^;\n]+)/i);
  if (unquoted?.[1]) return unquoted[1].trim().replace(/^"|"$/g, '');
  const utf8Star = header.match(/filename\*=UTF-8''([^;\n]+)/i);
  if (utf8Star?.[1]) {
    try {
      return decodeURIComponent(utf8Star[1].trim());
    } catch (_) {
      return utf8Star[1].trim();
    }
  }
  return null;
}

async function downloadReportPdf(relativePath, queryParamsRecord, defaultFilename) {
  const queryString = new URLSearchParams(
    Object.fromEntries(
      Object.entries(queryParamsRecord).map(([k, v]) => [k, String(v)]),
    ),
  ).toString();
  const path = `${relativePath}${queryString ? `?${queryString}` : ''}`;
  const url = getApiUrl(path);

  const controller = new AbortController();
  const pdfTimeoutMs = Math.max(config.api.timeout, 120000);
  const timeoutId = setTimeout(() => controller.abort(), pdfTimeoutMs);

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders({
      Accept: 'application/pdf, application/octet-stream, */*',
    }),
    signal: controller.signal,
  });
  clearTimeout(timeoutId);

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    if (contentType.includes('application/json')) {
      try {
        const errBody = await response.json();
        message = errBody.message || errBody.error || message;
      } catch (_) {
        // keep default message
      }
    } else {
      try {
        const text = await response.text();
        if (text && text.length < 500 && !/^%PDF/.test(text)) message = text;
      } catch (_) {
        // keep default message
      }
    }
    return { success: false, error: message };
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename =
    parseFilenameFromContentDisposition(disposition) || defaultFilename;

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);

  return { success: true, filename };
}

function normalizePdfPagination(params) {
  const skipRaw =
    params.skip !== undefined && params.skip !== null && params.skip !== ''
      ? Number(params.skip)
      : 0;
  const skip = Number.isFinite(skipRaw) && skipRaw >= 0 ? skipRaw : 0;

  const parsedLimit = Number(params.limit);
  let limit =
    Number.isFinite(parsedLimit) && parsedLimit >= 1 ? parsedLimit : 1000;
  limit = Math.min(10000, Math.max(1, Math.floor(limit)));

  return { skip, limit };
}

/**
 * Get student class list report
 * GET /v1/reports/class-list
 * @param {Object} params - Query params: center, field, skip, limit
 * @returns {Promise<{success: boolean, data?: Array, pagination?: Object, error?: string}>}
 */
export const getClassList = async (params = {}) => {
  try {
    const queryParams = {};

    if (params.center) queryParams.center = params.center;
    if (params.field) queryParams.field = params.field;
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/reports/class-list${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(endpoint);

    if (response.status === 'success' && Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination || null,
        message: response.message || 'Class list retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve class list',
      data: [],
      pagination: null,
    };
  } catch (error) {
    console.error('Get class list error:', error);

    let errorMessage = 'Failed to retrieve class list. Please try again.';

    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      pagination: null,
    };
  }
};

/**
 * Get tuition payments report
 * GET /v1/reports/tuition-payments
 * @param {Object} params - Query params: center, field, phase, status_filter, prepa_session, skip, limit
 * @returns {Promise<{success: boolean, data?: Array, pagination?: Object, error?: string}>}
 */
export const getTuitionPayments = async (params = {}) => {
  try {
    const queryParams = {};

    if (params.center) queryParams.center = params.center;
    if (params.field) queryParams.field = params.field;
    if (params.phase) queryParams.phase = params.phase;
    if (params.status_filter) queryParams.status_filter = params.status_filter;
    if (params.prepa_session) queryParams.prepa_session = params.prepa_session;
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/reports/tuition-payments${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(endpoint);

    if (response.status === 'success' && Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination || null,
        message: response.message || 'Tuition payments report retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve tuition payments report',
      data: [],
      pagination: null,
    };
  } catch (error) {
    console.error('Get tuition payments report error:', error);

    let errorMessage = 'Failed to retrieve tuition payments report. Please try again.';

    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      pagination: null,
    };
  }
};

/**
 * Get student and tuition analytics summary
 * GET /v1/reports/student-tuition-summary
 */
export const getStudentTuitionSummary = async () => {
  try {
    const response = await api.get('/v1/reports/student-tuition-summary');

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Student and tuition summary retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve student and tuition summary',
      data: null,
    };
  } catch (error) {
    console.error('Get student tuition summary error:', error);

    let errorMessage = 'Failed to retrieve student and tuition summary. Please try again.';

    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Download student class list as PDF (GET /v1/reports/class-list/pdf)
 */
export const downloadClassListPdf = async (params = {}) => {
  try {
    const queryParams = {};
    if (params.center) queryParams.center = params.center;
    if (params.field) queryParams.field = params.field;
    const { skip, limit } = normalizePdfPagination(params);
    queryParams.skip = skip;
    queryParams.limit = limit;

    return await downloadReportPdf(
      '/v1/reports/class-list/pdf',
      queryParams,
      'class-list-report.pdf',
    );
  } catch (error) {
    console.error('Download class list PDF error:', error);
    const errorMessage =
      error?.name === 'AbortError'
        ? 'Download timed out. Try a smaller export limit.'
        : error?.message || 'Failed to download PDF. Please try again.';
    return { success: false, error: errorMessage };
  }
};

/**
 * Download tuition payments report as PDF (GET /v1/reports/tuition-payments/pdf)
 * @param {Object} params - Same query params as getTuitionPayments; limit up to 10000 for export.
 * @returns {Promise<{success: boolean, filename?: string, error?: string}>}
 */
export const downloadTuitionPaymentsPdf = async (params = {}) => {
  try {
    const queryParams = {};

    if (params.center) queryParams.center = params.center;
    if (params.field) queryParams.field = params.field;
    if (params.phase) queryParams.phase = params.phase;
    if (params.status_filter) queryParams.status_filter = params.status_filter;
    if (params.prepa_session) queryParams.prepa_session = params.prepa_session;

    const { skip, limit } = normalizePdfPagination(params);
    queryParams.skip = skip;
    queryParams.limit = limit;

    return await downloadReportPdf(
      '/v1/reports/tuition-payments/pdf',
      queryParams,
      'tuition-payments-report.pdf',
    );
  } catch (error) {
    console.error('Download tuition PDF error:', error);
    const errorMessage =
      error?.name === 'AbortError'
        ? 'Download timed out. Try a smaller export limit.'
        : error?.message || 'Failed to download PDF. Please try again.';
    return { success: false, error: errorMessage };
  }
};

const reportsService = {
  getClassList,
  getTuitionPayments,
  getStudentTuitionSummary,
  downloadClassListPdf,
  downloadTuitionPaymentsPdf,
};

export default reportsService;
